import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";

export default function QualifyDialog({ listingId, agentName }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [sessionId] = useState(`qsess_${Math.random().toString(36).slice(2, 10)}`);
  const [optIn, setOptIn] = useState("yes");
  const [neighborhood, setNeighborhood] = useState("");
  const [budget, setBudget] = useState("");
  const [timeline, setTimeline] = useState("Immediate");
  const [outcome, setOutcome] = useState(null);

  const submit = async (s, answer) => {
    try {
      const { data } = await api.post("/leads/qualify", {
        listing_id: listingId,
        session_id: sessionId,
        step: s,
        answer,
      });
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Qualification step failed.");
      return null;
    }
  };

  const next = async () => {
    if (step === 0) {
      const r = await submit("opt_in", optIn);
      if (!r) return;
      if (!r.opt_in) {
        setOutcome(r);
        setStep(4);
        return;
      }
      setStep(1);
    } else if (step === 1) {
      if (!neighborhood) return toast.error("Please enter a neighborhood.");
      const r = await submit("neighborhood", neighborhood);
      if (r) setStep(2);
    } else if (step === 2) {
      if (!budget) return toast.error("Please share an approximate budget.");
      const r = await submit("budget", budget);
      if (r) setStep(3);
    } else if (step === 3) {
      const r = await submit("timeline", timeline);
      if (r) { setOutcome(r); setStep(4); }
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setStep(0); setOutcome(null); } }}>
      <DialogTrigger asChild>
        <Button size="sm" className="rounded-full bg-sg-navy hover:bg-sg-navy-700 text-white" data-testid={`inquire-btn-${listingId}`}>Inquire via WhatsApp</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="qualify-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            {step === 4 ? "We're on it." : `Qualify with ${agentName}`}
          </DialogTitle>
        </DialogHeader>

        {step === 0 && (
          <div className="space-y-3 py-2">
            <p className="text-sm text-[#64748B]">
              We use WhatsApp to send you the brochure and next steps. May we send you property updates?
            </p>
            <RadioGroup value={optIn} onValueChange={setOptIn} data-testid="qualify-optin">
              <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="yes" /> Yes, opt me in</label>
              <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="no" /> Not right now</label>
            </RadioGroup>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-3 py-2">
            <Label className="overline text-[#64748B]">Which Pune neighborhood?</Label>
            <Input value={neighborhood} onChange={(e) => setNeighborhood(e.target.value)} placeholder="Baner, Kharadi, Wakad…" data-testid="qualify-neighborhood" />
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3 py-2">
            <Label className="overline text-[#64748B]">Approximate budget</Label>
            <Input value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="e.g., 1.2 Cr" data-testid="qualify-budget" />
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3 py-2">
            <Label className="overline text-[#64748B]">Timeline</Label>
            <RadioGroup value={timeline} onValueChange={setTimeline} data-testid="qualify-timeline">
              <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="Immediate" /> Immediate</label>
              <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="3-6 months" /> 3–6 months</label>
              <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="Just Exploring" /> Just Exploring</label>
            </RadioGroup>
          </div>
        )}

        {step === 4 && outcome && (
          <div className="space-y-3 py-2" data-testid="qualify-outcome">
            <p className="text-sm">
              {outcome.status === "SiteVisitPending" && "🚀 The agent will reach out within the hour to schedule a site visit."}
              {outcome.status === "Nurture" && "📩 Sending you our project brochure shortly. We'll check in periodically."}
              {outcome.status === "MidTermFollowUp" && "📅 We'll follow up in a couple of weeks with curated options."}
              {outcome.status === "OptedOut" && "Got it — we won't message you. You can revisit this listing anytime."}
              {outcome.status === "Qualified" && "Thanks. The agent will be in touch."}
            </p>
            <p className="overline text-[#64748B]">STATUS · {outcome.status}</p>
          </div>
        )}

        {step < 4 && (
          <Button onClick={next} className="w-full rounded-full bg-sg-navy hover:bg-sg-navy-700 text-white" data-testid="qualify-next-btn">
            {step === 3 ? "Submit" : "Continue"}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
